import { LatLng, Alert, HazardType, NewsItem, Language } from "../types";
import { getCyberLocationName } from "./locationService";
import { generateDisasterResumeWithAI, resumeToAlert, RawTomTomIncident } from "./aiAgentService";

/**
 * INTELLIGENCE SERVICE (AI-ENHANCED ENGINE)
 * Pipeline: Fetch -> AI Parse -> Trust Score -> Persistence
 */

// 1. TRUST SCORING LOGIC
const getTrustScore = (source: string): number => {
    const trusted = ['Hespress', 'Medi1TV', 'MAP', 'GDACS', 'USGS', 'SkyNet Weather', 'CityOS'];
    const medium = ['Twitter', 'Local Reports', 'Facebook Groups', 'SOS Beacon'];

    if (trusted.some(t => source.includes(t))) return 0.9 + (Math.random() * 0.1); // 90-100%
    if (medium.some(t => source.includes(t))) return 0.6 + (Math.random() * 0.2); // 60-80%
    return 0.3 + (Math.random() * 0.3); // 30-60% (Unknown)
};

// 2. MOCK DATA SOURCES with RAW TEXT
// We put "raw" text here to simulate what we scrape
const MOCK_RAW_FEED = [
    {
        text: "⚡ LIVE (13 Feb 14:02): Oued Sebou level critical (+4.2m). Dam release scheduled at 16:00. EVAC ZONE: Kenitra Lowlands.",
        source: "NAJA AI / ABHS",
        loc: { lat: 34.26, lng: -6.58 } // Kenitra
    },
    {
        text: "SNOW ALERT: Tizi n'Test pass closed. 45cm snow accumulation. 12 vehicles trapped. Drone airlift initiated.",
        source: "DPE Taroudant",
        loc: { lat: 30.86, lng: -8.37 } // Tizi n'Test
    },
    {
        text: "LANDSLIDE WARNING: Soil saturation at 98% in Chefchaouen sector. Slope instability detected near Bab Taza. EVACUATE.",
        source: "NAJA Geotech Grid",
        loc: { lat: 35.17, lng: -5.26 } // Chefchaouen
    },
    {
        text: "INFRASTRUCTURE: Casa-Settat High Speed Line (LGV) speed reduced due to high winds (80km/h). Delays exp +20min.",
        source: "ONCF Traffic",
        loc: { lat: 33.57, lng: -7.58 } // Casablanca
    },
    {
        text: "STORM RELIEF: Army helicopters dropping supplies to 12 isolated douars in Rif mountains. Roads blocked by mudslides.",
        source: "FAR Maroc",
        loc: { lat: 34.96, lng: -5.29 } // Brikcha
    },
    {
        text: "MISSING PERSON: 'Youssef', 72yo, Alzheimer's. Last ping: Medina Fez, Blue Gate sector. Grid search active.",
        source: "Family / Police",
        loc: { lat: 34.06, lng: -4.97 } // Fez
    }
];

// 3. AI ANALYSIS ENGINE (Rules-based Mock)
const analyzeRawFeed = (text: string): {
    type: HazardType,
    severity: 'critical' | 'high' | 'medium' | 'low',
    tips: string[],
    isRedAlert: boolean
} => {
    const lower = text.toLowerCase();

    // RED ALERTS (Critical Disasters)
    if (lower.includes('earthquake') && (lower.includes('damage') || lower.includes('magnitude'))) {
        return {
            type: HazardType.EARTHQUAKE,
            severity: 'critical',
            isRedAlert: true,
            tips: [
                "DROP, COVER, and HOLD ON immediately.",
                "Stay away from windows and heavy furniture.",
                "If outside, move to an open area away from buildings.",
                "Execute the 'Triangle of Life' if trapped."
            ]
        };
    }

    if (lower.includes('flood') && lower.includes('warning')) {
        return {
            type: HazardType.FLOOD,
            severity: 'critical',
            isRedAlert: true,
            tips: [
                "Move to higher ground immediately.",
                "Do NOT walk, swim, or drive through flood waters.",
                "Turn off electricity and gas if safe to do so.",
                "Prepare go-bag with documents and water."
            ]
        };
    }

    if (lower.includes('heat') && lower.includes('extreme')) {
        return {
            type: HazardType.WEATHER,
            severity: 'high',
            isRedAlert: false, // High but not immediate "Death" red alert unless verified
            tips: [
                "Stay hydrated. Drink water even if not thirsty.",
                "Avoid direct sunlight between 11 AM and 3 PM.",
                "Check on elderly neighbors."
            ]
        };
    }

    // STANDARD HAZARDS
    if (lower.includes('traffic') || lower.includes('collision')) {
        return {
            type: HazardType.TRAFFIC,
            severity: 'medium',
            isRedAlert: false,
            tips: ["Use alternate routes.", "Expect delays of 20-30 minutes."]
        };
    }

    if (lower.includes('road closed')) {
        return {
            type: HazardType.TRAFFIC,
            severity: 'high',
            isRedAlert: false,
            tips: ["Do not attempt to bypass barricades.", "Follow detour signs."]
        };
    }

    // Default
    return {
        type: HazardType.CIVIL,
        severity: 'low',
        isRedAlert: false,
        tips: ["Stay alert and follow local news."]
    };
};


import { getVerifiedHazards } from "./hazardSyncService";

// ...

export const fetchRegionalIntelligence = async (
    center: LatLng,
    radiusKm: number = 50,
    lang: Language = 'en'
): Promise<{ hazards: Alert[], news: NewsItem[] }> => {



    // Simulate Network/Processing Delay (The "AI Thinking" time)
    await new Promise(r => setTimeout(r, 800));

    const locationName = await getCyberLocationName(center.lat, center.lng, lang);

    // Generate Results
    const hazards: Alert[] = [];
    const news: NewsItem[] = [];

    // REAL INTELLIGENCE: Fetch Verified Hazards from Supabase
    try {
        const verified = await getVerifiedHazards();
        hazards.push(...verified);

    } catch (e) {
        console.error("Failed to load verified hazards", e);
    }


    // RESTORED FOR VISUALIZATION (User Request):
    // If no real hazards found, simulate CIVIL intel for UI verification
    if (hazards.length === 0) {
        hazards.push({
            id: '2042',
            type: HazardType.TRAFFIC,
            severity: 'medium',
            location: locationName || 'Martil Center',
            // Jitter for visual separation (approx 50-100m)
            coordinates: {
                lat: 35.6186 + (Math.random() * 0.002 - 0.001),
                lng: -5.2732 + (Math.random() * 0.002 - 0.001)
            },
            timestamp: new Date().toISOString(),
            time: 'Just now',
            distance: '0.5km',
            isOfficial: true,
            source: 'Traffic Control (Sim)',
            truthScore: 90,
            description: 'Moderate congestion detected on main artery. Expect delays.',
            advice: ['Use alternate routes'],
            imageUrl: 'https://images.unsplash.com/photo-1455459303976-71343cd0929b?w=800&q=80'
        });

        news.push({
            id: '3089',
            headline: 'Urban Mobility Upgrade',
            source: 'CityOS',
            author: 'System Admin', // Added
            time: '1h ago',
            category: 'SYSTEM',
            location: 'Central Sector',
            description: 'Main artery optimization complete. Traffic flow increased by 15%.',
            advice: ['Use Main St for faster commute', 'Update GPS firmware'], // Added
            realityScore: 10,
            // Jitter for visual separation
            coordinates: {
                lat: 35.6150 + (Math.random() * 0.002 - 0.001),
                lng: -5.2760 + (Math.random() * 0.002 - 0.001)
            }
        });
    }

    return { hazards, news };
};
